import React, { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus, Trash2, Shield, Zap, Eye, FlaskConical, Cloud as CloudIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function Network() {
  return (
    <Layout title="Network Defenses" subtitle="DDOS · DPI · KNOCK · M365">
      <Tabs defaultValue="ddos" className="w-full">
        <TabsList className="bg-zinc-900 rounded-sm" data-testid="net-tabs">
          <TabsTrigger value="ddos" data-testid="tab-ddos">DDoS Protection</TabsTrigger>
          <TabsTrigger value="dpi" data-testid="tab-dpi">DPI Rules</TabsTrigger>
          <TabsTrigger value="knock" data-testid="tab-knock">Port Knocking</TabsTrigger>
          <TabsTrigger value="m365" data-testid="tab-m365">M365 Import</TabsTrigger>
        </TabsList>
        <TabsContent value="ddos" className="pt-4"><DdosTab /></TabsContent>
        <TabsContent value="dpi" className="pt-4"><DpiTab /></TabsContent>
        <TabsContent value="knock" className="pt-4"><KnockTab /></TabsContent>
        <TabsContent value="m365" className="pt-4"><M365Tab /></TabsContent>
      </Tabs>
    </Layout>
  );
}

// ===== DDoS =====
function DdosTab() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const empty = { name: "", syn_flood_threshold_pps: 5000, udp_flood_threshold_pps: 10000, icmp_flood_threshold_pps: 1000,
    connection_limit_per_ip: 100, rate_limit_rps_per_ip: 200, auto_blackhole: true, blackhole_duration_minutes: 15,
    geo_blocklist: "", enabled: true };
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/ddos/profiles"); setItems(data); }
    catch (err) { console.error("ddos load failed", err); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      const body = { ...form, geo_blocklist: typeof form.geo_blocklist === "string" ? form.geo_blocklist.split(",").map(s => s.trim()).filter(Boolean) : (form.geo_blocklist || []) };
      await api.post("/ddos/profiles", body); toast.success("Profile saved"); setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/ddos/profiles/${id}`); toast.success("Removed"); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest" data-testid="ddos-count">{items.length} PROFILES</div>
        {user?.role === "admin" && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setForm(empty); }}>
            <DialogTrigger asChild><Button data-testid="ddos-new" className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm gap-2"><Plus className="w-4 h-4" />NEW PROFILE</Button></DialogTrigger>
            <DialogContent className="bg-zinc-950 border-zinc-800 rounded-sm max-w-xl">
              <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Zap className="w-4 h-4 text-amber-400" /> DDoS Profile</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3 py-2">
                <div className="col-span-2"><L>Name</L><Input data-testid="ddos-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm" /></div>
                <Num label="SYN flood pps" testid="ddos-syn" value={form.syn_flood_threshold_pps} set={(v) => setForm({ ...form, syn_flood_threshold_pps: v })} />
                <Num label="UDP flood pps" testid="ddos-udp" value={form.udp_flood_threshold_pps} set={(v) => setForm({ ...form, udp_flood_threshold_pps: v })} />
                <Num label="ICMP flood pps" testid="ddos-icmp" value={form.icmp_flood_threshold_pps} set={(v) => setForm({ ...form, icmp_flood_threshold_pps: v })} />
                <Num label="Conn limit / IP" testid="ddos-conn" value={form.connection_limit_per_ip} set={(v) => setForm({ ...form, connection_limit_per_ip: v })} />
                <Num label="Rate limit rps / IP" testid="ddos-rps" value={form.rate_limit_rps_per_ip} set={(v) => setForm({ ...form, rate_limit_rps_per_ip: v })} />
                <Num label="Blackhole minutes" testid="ddos-bh" value={form.blackhole_duration_minutes} set={(v) => setForm({ ...form, blackhole_duration_minutes: v })} />
                <div className="col-span-2"><L>Geo Blocklist (country codes, comma-separated)</L>
                  <Input data-testid="ddos-geo" value={form.geo_blocklist} onChange={(e) => setForm({ ...form, geo_blocklist: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" placeholder="KP, IR, RU" /></div>
                <div className="col-span-2 grid grid-cols-2 gap-3">
                  <ToggleBox label="Auto Blackhole" testid="ddos-auto" value={form.auto_blackhole} set={(v) => setForm({ ...form, auto_blackhole: v })} />
                  <ToggleBox label="Enabled" testid="ddos-enabled" value={form.enabled} set={(v) => setForm({ ...form, enabled: v })} />
                </div>
              </div>
              <DialogFooter><Button data-testid="ddos-submit" onClick={submit} className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm">CREATE</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="ddos-list">
        {items.map((p) => (
          <div key={p.profile_id} data-testid={`ddos-card-${p.profile_id}`} className="border border-zinc-800 rounded-sm bg-zinc-900/60 p-4 fade-up">
            <div className="flex justify-between mb-3">
              <div className="font-display text-lg font-bold">{p.name}</div>
              <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border border-amber-500/40 bg-amber-500/10 text-amber-400 rounded-sm">DDOS-GUARD</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center mb-3">
              <Stat label="SYN/s" v={p.syn_flood_threshold_pps} />
              <Stat label="UDP/s" v={p.udp_flood_threshold_pps} />
              <Stat label="ICMP/s" v={p.icmp_flood_threshold_pps} />
            </div>
            <div className="grid grid-cols-2 gap-2 text-center mb-3">
              <Stat label="CONN/IP" v={p.connection_limit_per_ip} />
              <Stat label="RATE rps/IP" v={p.rate_limit_rps_per_ip} />
            </div>
            {p.geo_blocklist?.length > 0 && (
              <div className="text-[10px] font-mono text-zinc-400 mb-3">
                <span className="text-zinc-500 uppercase tracking-widest mr-2">geo-block:</span>
                {p.geo_blocklist.join(", ")}
              </div>
            )}
            {user?.role === "admin" && (
              <Button size="sm" variant="ghost" data-testid={`ddos-del-${p.profile_id}`} onClick={() => del(p.profile_id)} className="gap-1.5 text-red-400"><Trash2 className="w-3.5 h-3.5" />Delete</Button>
            )}
          </div>
        ))}
        {items.length === 0 && <div className="text-zinc-500 font-mono text-sm">no profiles yet.</div>}
      </div>
    </div>
  );
}

// ===== DPI =====
function DpiTab() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const empty = { name: "", layer: "L7", matcher_type: "regex", pattern: "", action: "alert", severity: "medium", enabled: true, description: "" };
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/dpi/rules"); setItems(data); }
    catch (err) { console.error("dpi load failed", err); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try { await api.post("/dpi/rules", form); toast.success("Rule saved"); setOpen(false); setForm(empty); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/dpi/rules/${id}`); toast.success("Removed"); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest" data-testid="dpi-count">{items.length} DPI RULES</div>
        {user?.role === "admin" && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setForm(empty); }}>
            <DialogTrigger asChild><Button data-testid="dpi-new" className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm gap-2"><Plus className="w-4 h-4" />NEW DPI RULE</Button></DialogTrigger>
            <DialogContent className="bg-zinc-950 border-zinc-800 rounded-sm max-w-xl">
              <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Eye className="w-4 h-4 text-cyan-400" /> Deep Packet Inspection Rule</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3 py-2">
                <div className="col-span-2"><L>Name</L><Input data-testid="dpi-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm" /></div>
                <div><L>Layer</L><Sel testid="dpi-layer" value={form.layer} onChange={(v) => setForm({ ...form, layer: v })} options={["L4", "L7"]} /></div>
                <div><L>Matcher Type</L><Sel testid="dpi-mtype" value={form.matcher_type} onChange={(v) => setForm({ ...form, matcher_type: v })} options={["regex", "literal", "protocol"]} /></div>
                <div className="col-span-2"><L>Pattern</L><Textarea data-testid="dpi-pattern" value={form.pattern} onChange={(e) => setForm({ ...form, pattern: e.target.value })} rows={3} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono text-xs" placeholder="e.g. \\${jndi:" /></div>
                <div><L>Action</L><Sel testid="dpi-action" value={form.action} onChange={(v) => setForm({ ...form, action: v })} options={["alert", "block", "log"]} /></div>
                <div><L>Severity</L><Sel testid="dpi-sev" value={form.severity} onChange={(v) => setForm({ ...form, severity: v })} options={["low", "medium", "high", "critical"]} /></div>
                <div className="col-span-2"><ToggleBox label="Enabled" testid="dpi-enabled" value={form.enabled} set={(v) => setForm({ ...form, enabled: v })} /></div>
              </div>
              <DialogFooter><Button data-testid="dpi-submit" onClick={submit} className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm">CREATE</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
      <div className="border border-zinc-800 rounded-sm bg-zinc-900/40 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 bg-zinc-900/60 border-b border-zinc-800">
            <tr><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Layer</th><th className="text-left px-4 py-3">Matcher</th><th className="text-left px-4 py-3">Pattern</th><th className="text-left px-4 py-3">Action</th><th className="text-left px-4 py-3">Severity</th><th className="text-right px-4 py-3"></th></tr>
          </thead>
          <tbody data-testid="dpi-table">
            {items.map((r) => (
              <tr key={r.rule_id} className="border-b border-zinc-800/70 hover:bg-zinc-900/60 font-mono text-xs">
                <td className="px-4 py-3 text-zinc-200">{r.name}</td>
                <td className="px-4 py-3 text-cyan-400">{r.layer}</td>
                <td className="px-4 py-3 text-zinc-400">{r.matcher_type}</td>
                <td className="px-4 py-3 text-amber-300 max-w-xs whitespace-normal break-words">{r.pattern}</td>
                <td className="px-4 py-3 uppercase">{r.action}</td>
                <td className="px-4 py-3"><span className={`px-1.5 py-0.5 border rounded-sm uppercase tracking-widest text-[10px] sev-${r.severity}`}>{r.severity}</span></td>
                <td className="px-4 py-3 text-right">
                  {user?.role === "admin" && <button data-testid={`dpi-del-${r.rule_id}`} onClick={() => del(r.rule_id)} className="text-zinc-400 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>}
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="text-center text-zinc-500 py-10 font-mono">no DPI rules.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ===== Knock =====
function KnockTab() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [open, setOpen] = useState(false);
  const [tester, setTester] = useState(null);
  const [knocks, setKnocks] = useState("");
  const [srcIp, setSrcIp] = useState("203.0.113.10");
  const empty = { name: "", sequence: "tcp/1000,udp/2000,tcp/3000", open_port: 22, open_protocol: "tcp", open_duration_seconds: 30, enabled: true };
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    try {
      const [s, a] = await Promise.all([api.get("/knock/sequences"), api.get("/knock/attempts")]);
      setItems(s.data); setAttempts(a.data);
    } catch (err) { console.error("knock load failed", err); }
  }, []);
  useEffect(() => { load(); const i = setInterval(load, 6000); return () => clearInterval(i); }, [load]);

  const submit = async () => {
    try {
      const body = { ...form, sequence: typeof form.sequence === "string" ? form.sequence.split(",").map(s => s.trim()).filter(Boolean) : form.sequence };
      await api.post("/knock/sequences", body); toast.success("Sequence saved"); setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/knock/sequences/${id}`); toast.success("Removed"); load(); };
  const test = async () => {
    try {
      const k = knocks.split(",").map(s => s.trim()).filter(Boolean);
      const { data } = await api.post("/knock/test", { sequence_id: tester, source_ip: srcIp, knocks: k });
      data.match ? toast.success(`MATCH — port ${data.opened_port} opened from ${data.source_ip}`) : toast.error("Sequence mismatch — knock denied");
      setTester(null); setKnocks(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest" data-testid="knock-count">{items.length} SEQUENCES · {attempts.length} ATTEMPTS</div>
        {user?.role === "admin" && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setForm(empty); }}>
            <DialogTrigger asChild><Button data-testid="knock-new" className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm gap-2"><Plus className="w-4 h-4" />NEW SEQUENCE</Button></DialogTrigger>
            <DialogContent className="bg-zinc-950 border-zinc-800 rounded-sm max-w-md">
              <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Shield className="w-4 h-4 text-cyan-400" /> Knock Sequence</DialogTitle></DialogHeader>
              <div className="space-y-3 py-2">
                <div><L>Name</L><Input data-testid="knock-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm" /></div>
                <div><L>Sequence (proto/port, comma-separated)</L><Input data-testid="knock-seq" value={form.sequence} onChange={(e) => setForm({ ...form, sequence: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
                <div className="grid grid-cols-3 gap-2">
                  <div><L>Open Port</L><Input data-testid="knock-port" type="number" value={form.open_port} onChange={(e) => setForm({ ...form, open_port: parseInt(e.target.value || "0", 10) })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
                  <div><L>Proto</L><Sel testid="knock-proto" value={form.open_protocol} onChange={(v) => setForm({ ...form, open_protocol: v })} options={["tcp", "udp"]} /></div>
                  <div><L>Duration (s)</L><Input data-testid="knock-dur" type="number" value={form.open_duration_seconds} onChange={(e) => setForm({ ...form, open_duration_seconds: parseInt(e.target.value || "0", 10) })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
                </div>
              </div>
              <DialogFooter><Button data-testid="knock-submit" onClick={submit} className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm">CREATE</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 lg:col-span-7 border border-zinc-800 rounded-sm bg-zinc-900/40">
          <div className="px-4 py-2 border-b border-zinc-800 text-[10px] font-mono uppercase tracking-widest text-zinc-400">SEQUENCES</div>
          <div className="divide-y divide-zinc-800/70" data-testid="knock-list">
            {items.map((s) => (
              <div key={s.sequence_id} data-testid={`knock-row-${s.sequence_id}`} className="px-4 py-3 flex items-center gap-3 font-mono text-xs">
                <div className="flex-1">
                  <div className="text-zinc-200">{s.name}</div>
                  <div className="text-cyan-400 mt-0.5">{(s.sequence || []).join(" → ")} ⇒ <span className="text-amber-400">open {s.open_protocol}/{s.open_port}</span> for {s.open_duration_seconds}s</div>
                </div>
                <div className="text-right">
                  <div className="text-emerald-400">{s.successful_unlocks} ok</div>
                  <div className="text-red-400">{s.failed_attempts} fail</div>
                </div>
                <Button size="sm" variant="ghost" data-testid={`knock-test-${s.sequence_id}`} onClick={() => { setTester(s.sequence_id); setKnocks((s.sequence || []).join(", ")); }} className="gap-1.5 text-cyan-400"><FlaskConical className="w-3.5 h-3.5" />Test</Button>
                {user?.role === "admin" && <Button size="sm" variant="ghost" data-testid={`knock-del-${s.sequence_id}`} onClick={() => del(s.sequence_id)} className="gap-1.5 text-red-400"><Trash2 className="w-3.5 h-3.5" /></Button>}
              </div>
            ))}
            {items.length === 0 && <div className="text-zinc-500 text-sm font-mono px-4 py-6">no sequences.</div>}
          </div>
        </div>

        <div className="col-span-12 lg:col-span-5 border border-zinc-800 rounded-sm bg-zinc-900/40">
          <div className="px-4 py-2 border-b border-zinc-800 text-[10px] font-mono uppercase tracking-widest text-zinc-400">RECENT ATTEMPTS</div>
          <div className="divide-y divide-zinc-800/70 max-h-80 overflow-y-auto" data-testid="knock-attempts-list">
            {attempts.slice(0, 30).map((a) => (
              <div key={a.attempt_id} className="px-4 py-2 font-mono text-[11px] flex items-center gap-2">
                <span className="text-zinc-500">{a.timestamp.slice(11, 19)}</span>
                <span className={a.match ? "live-dot" : "live-dot red"} />
                <span className="text-cyan-400">{a.source_ip}</span>
                <span className={a.match ? "text-emerald-400" : "text-red-400"}>{a.match ? "OPENED" : "DENIED"}</span>
              </div>
            ))}
            {attempts.length === 0 && <div className="text-zinc-500 text-sm font-mono px-4 py-6">no attempts yet.</div>}
          </div>
        </div>
      </div>

      {/* Knock Tester Dialog */}
      <Dialog open={!!tester} onOpenChange={(v) => !v && setTester(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 rounded-sm max-w-md">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><FlaskConical className="w-4 h-4 text-cyan-400" /> Knock Tester</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><L>Source IP</L><Input data-testid="knock-test-ip" value={srcIp} onChange={(e) => setSrcIp(e.target.value)} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
            <div><L>Knock sequence to send (comma-separated proto/port)</L><Input data-testid="knock-test-seq" value={knocks} onChange={(e) => setKnocks(e.target.value)} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
          </div>
          <DialogFooter><Button data-testid="knock-test-submit" onClick={test} className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm">SEND KNOCKS</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===== M365 import =====
function M365Tab() {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [instance, setInstance] = useState("Worldwide");
  const [services, setServices] = useState({ Exchange: true, SharePoint: true, Skype: true, Common: true });
  const [createPol, setCreatePol] = useState(true);
  const [cloud, setCloud] = useState("any");
  const [manualJson, setManualJson] = useState("");

  const importLive = async () => {
    setBusy(true);
    try {
      const selectedSvc = Object.entries(services).filter(([, v]) => v).map(([k]) => k);
      const { data } = await api.post("/m365/import", { instance, services: selectedSvc, create_policies: createPol, cloud });
      setResult(data); toast.success(`Pulled ${data.ip_count} IPs · created ${data.policies_created} policies`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const importManual = async () => {
    setBusy(true);
    try {
      let parsed;
      try { parsed = JSON.parse(manualJson); }
      catch { toast.error("Invalid JSON"); setBusy(false); return; }
      if (!Array.isArray(parsed)) { toast.error("Expected an array of endpoint objects"); setBusy(false); return; }
      const selectedSvc = Object.entries(services).filter(([, v]) => v).map(([k]) => k);
      const { data } = await api.post("/m365/import/manual", { payload: parsed, services: selectedSvc, create_policies: createPol, cloud });
      setResult(data); toast.success(`Imported ${data.ip_count} IPs · created ${data.policies_created} policies`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (user?.role !== "admin") {
    return <div className="text-zinc-500 font-mono text-sm">Admin role required to import M365 endpoints.</div>;
  }

  return (
    <div className="grid grid-cols-12 gap-4">
      <div className="col-span-12 lg:col-span-7 border border-zinc-800 rounded-sm bg-zinc-900/60 p-5 fade-up">
        <div className="flex items-center gap-2 mb-3 text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-400"><CloudIcon className="w-3.5 h-3.5" />// MICROSOFT 365 ENDPOINT IMPORT</div>
        <h3 className="font-display text-xl font-bold mb-2">Auto-import allow-list policies</h3>
        <p className="text-zinc-400 text-sm mb-4">Pull the latest Office 365 endpoint JSON directly from Microsoft, or paste your own.</p>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div><L>Instance</L><Sel testid="m365-instance" value={instance} onChange={setInstance} options={["Worldwide", "China", "Germany", "USGovDoD", "USGovGCCHigh"]} /></div>
          <div><L>Cloud Scope</L><Sel testid="m365-cloud" value={cloud} onChange={setCloud} options={["any", "aws", "azure", "gcp", "oracle"]} /></div>
        </div>
        <div className="mb-3">
          <L>Service Areas</L>
          <div className="grid grid-cols-4 gap-2 mt-1">
            {Object.keys(services).map((k) => (
              <ToggleBox key={k} label={k} testid={`m365-svc-${k}`} value={services[k]} set={(v) => setServices({ ...services, [k]: v })} />
            ))}
          </div>
        </div>
        <ToggleBox label="Auto-create allow policies" testid="m365-create" value={createPol} set={setCreatePol} />
        <div className="flex gap-2 mt-4">
          <Button data-testid="m365-fetch" onClick={importLive} disabled={busy} className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm font-semibold">
            {busy ? "FETCHING..." : "FETCH FROM MICROSOFT"}
          </Button>
        </div>
      </div>

      <div className="col-span-12 lg:col-span-5 border border-zinc-800 rounded-sm bg-zinc-900/60 p-5 fade-up">
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-amber-400 mb-3">// MANUAL JSON</div>
        <Textarea
          data-testid="m365-manual-json"
          value={manualJson} onChange={(e) => setManualJson(e.target.value)}
          rows={10}
          placeholder='[{"id":1,"serviceArea":"Exchange","ips":["13.107.6.152/31"],"tcpPorts":"443"}]'
          className="bg-zinc-950 border-zinc-800 rounded-sm font-mono text-[10px]"
        />
        <Button data-testid="m365-manual-submit" onClick={importManual} disabled={busy || !manualJson} className="mt-3 w-full bg-amber-500 hover:bg-amber-400 text-zinc-950 rounded-sm font-semibold">
          IMPORT JSON
        </Button>
      </div>

      {result && (
        <div className="col-span-12 border border-cyan-500/40 bg-cyan-500/5 rounded-sm p-4 fade-up" data-testid="m365-result">
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-400 mb-2">// IMPORT RESULT</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="IPs IMPORTED" v={result.ip_count} />
            <Stat label="PORTS" v={result.port_count} />
            <Stat label="SERVICES" v={result.services?.length} />
            <Stat label="POLICIES CREATED" v={result.policies_created} />
          </div>
          <div className="text-[10px] font-mono text-zinc-500 mt-2">fetched {result.fetched_at?.slice(0, 19).replace("T", " ")} UTC</div>
        </div>
      )}
    </div>
  );
}

// ===== Helpers =====
function L({ children }) { return <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">{children}</Label>; }
function Sel({ value, onChange, options, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono uppercase text-xs"><SelectValue /></SelectTrigger>
      <SelectContent className="bg-zinc-950 border-zinc-800">{options.map((o) => <SelectItem key={o} value={o} className="font-mono uppercase text-xs">{o}</SelectItem>)}</SelectContent>
    </Select>
  );
}
function Num({ label, value, set, testid }) {
  return (<div><L>{label}</L><Input data-testid={testid} type="number" value={value} onChange={(e) => set(parseInt(e.target.value || "0", 10))} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>);
}
function ToggleBox({ label, value, set, testid }) {
  return (<div className="flex items-center justify-between border border-zinc-800 rounded-sm px-3 py-2 bg-zinc-900"><span className="text-sm">{label}</span><Switch data-testid={testid} checked={value} onCheckedChange={set} /></div>);
}
function Stat({ label, v }) {
  return (<div className="border border-zinc-800 rounded-sm bg-zinc-950 p-2 text-center"><div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">{label}</div><div className="font-mono text-sm text-zinc-200 whitespace-normal break-words">{v ?? "—"}</div></div>);
}
